import React, { useCallback, useEffect, useRef } from 'react';

import { View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { DrawerNavigationProp } from '@react-navigation/drawer';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { LeftArrow } from 'assets/images';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';
import { BACK_BUTTON_ID } from 'config/testID';

import { RouteNavigationParams } from 'router/types';

import { selectEpgPlayerFullscreen } from 'store/epg/selectors';
import {
    selectPlayerConfig,
    selectSpotlightVideoVisible,
} from 'store/player/selectors';

import setAccessibilityFocusTo from 'services/accessibility/setAccessibilityFocusTo';

import isFunction from 'utils/function';
import usePrevious from 'utils/hooks/usePrevious';
import { widthPercentageToDP } from 'utils/responsiveness';

import SvgButton from 'components/CustomButtons/SvgButton';

import styles from './styles';

type Props = {
    showSideMenuOnBack?: boolean;
    onPress?: () => void;
};

const {
    ACCESSIBILITY: {
        DETAIL_PAGE: { ACCSESSIBILITY_DETAIL_PAGE_BACK },
    },
} = LANGUAGE_KEYS;

function SimpleNavigationHeaderLeft({ showSideMenuOnBack, onPress }: Props) {
    const { t } = useTranslation();
    const stackNavigation =
        useNavigation<
            StackNavigationProp<
                RouteNavigationParams,
                keyof RouteNavigationParams
            >
        >();
    const drawerNavigation = useNavigation<DrawerNavigationProp<any>>();

    const { visible: playerVisible } = useSelector(selectPlayerConfig);
    const epgPlayerFullscreen = useSelector(selectEpgPlayerFullscreen);
    const spotlightPlayerVisible = useSelector(selectSpotlightVideoVisible);
    const prevEpgPlayerInFullscreen = usePrevious(epgPlayerFullscreen);

    const buttonRef = useRef(null);

    const fullscreenPlayerVisible =
        playerVisible || epgPlayerFullscreen || spotlightPlayerVisible;

    const handlePressBack = useCallback(() => {
        if (isFunction(onPress)) {
            onPress();
        } else {
            if (showSideMenuOnBack && isFunction(drawerNavigation.openDrawer)) {
                drawerNavigation.openDrawer();
            }

            stackNavigation.pop();
        }
    }, [showSideMenuOnBack, drawerNavigation, stackNavigation, onPress]);

    useEffect(() => {
        if (!epgPlayerFullscreen) {
            setTimeout(() => {
                setAccessibilityFocusTo(buttonRef);
            }, 110);
        }
    }, [epgPlayerFullscreen]);

    return (
        <View
            style={[
                styles.container,
                gStyles.headerDefaultPaddingLeft,
                gStyles.headerContainer,
            ]}
            importantForAccessibility={
                fullscreenPlayerVisible ? 'no-hide-descendants' : 'auto'
            }
            accessibilityElementsHidden={fullscreenPlayerVisible}
        >
            <SvgButton
                accessible
                accessibilityLabel={t(ACCSESSIBILITY_DETAIL_PAGE_BACK)}
                Svg={() => <LeftArrow width={widthPercentageToDP(17)} />}
                style={styles.buttonLeft}
                onPress={handlePressBack}
                testID={BACK_BUTTON_ID}
                ref={buttonRef}
            />
        </View>
    );
}

export default SimpleNavigationHeaderLeft;
